import { Typography } from '@components'
import { useAvailableCountries } from '@haus-storefront-react/hooks'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { ReactNode } from 'react'
import type { AddressData } from '../../types'
import { formatAddressForDisplay } from './customer-details.helpers'
import styles from './CustomerDetails.module.scss'

export interface AddressSectionProps {
  title: string
  readOnlyAriaLabel: string
  address: AddressData | null
  userIsLoggedIn: boolean
  /** If true, show section with error when logged in but no address. If false, hide section entirely. */
  showSectionWhenNoAddress: boolean
  guestContent: ReactNode
}

export function AddressSection({
  title,
  readOnlyAriaLabel,
  address,
  userIsLoggedIn,
  showSectionWhenNoAddress,
  guestContent,
}: AddressSectionProps) {
  const { t } = useTranslation()
  const { data: countries } = useAvailableCountries()
  const getCountryName = useMemo(() => {
    const map = new Map<string, string>(countries?.map((c) => [String(c.code), c.name]) ?? [])
    return (code: string) => map.get(code) ?? code
  }, [countries])
  const showSection =
    !userIsLoggedIn || (userIsLoggedIn && (address !== null || showSectionWhenNoAddress))
  if (!showSection) return null

  return (
    <div className={styles.formSection}>
      <Typography variant="L" as="h3">
        {title}
      </Typography>
      {userIsLoggedIn && address ? (
        <p className={styles.addressReadOnly} aria-label={readOnlyAriaLabel}>
          {formatAddressForDisplay(address, getCountryName)}
        </p>
      ) : userIsLoggedIn ? (
        <div className={styles.error} role="alert">
          <Typography variant="body">{t('checkout.customerDetails.noAddressError')}</Typography>
        </div>
      ) : (
        guestContent
      )}
    </div>
  )
}
